import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ReactNode, useRef } from 'react';
import { classNames } from '../../utils';
import { ModalActionData, ModalActions, ModalActionsAlignment } from './Modal';

interface Props {
  type: 'info' | 'warn' | 'error' | 'success';
  title?: string;
  description?: string;
  children?: ReactNode;
  open: boolean;
  onClose: () => void;
  actions?: ModalActionData[];
  actionsAlignment?: ModalActionsAlignment;
  width?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl';
}

export default function AlertModal({
  type = 'info',
  title,
  description,
  children,
  open,
  onClose,
  actions,
  actionsAlignment = 'end',
}: Props) {
  const primaryButtonRef = useRef(null);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div
                  className={classNames(
                    'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
                    type === 'info' ? 'bg-black-100' : '',
                    type === 'warn' ? 'bg-orange-100' : '',
                    type === 'error' ? 'bg-red-100' : '',
                    type === 'success' ? 'bg-green-100' : '',
                  )}
                >
                  {type === 'info' ? (
                    <InformationCircleIcon
                      className="text-black-600 h-6 w-6"
                      aria-hidden="true"
                    />
                  ) : (
                    <></>
                  )}
                  {type === 'warn' ? (
                    <ExclamationCircleIcon
                      className="h-6 w-6 text-orange-700"
                      aria-hidden="true"
                    />
                  ) : (
                    <></>
                  )}
                  {type === 'error' ? (
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  ) : (
                    <></>
                  )}
                  {type === 'success' ? (
                    <CheckCircleIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  ) : (
                    <></>
                  )}
                </div>
                <div className="mt-3 flex-1 self-center text-center sm:ml-4 sm:mt-0 sm:text-left">
                  {title ? (
                    <DialogTitle
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      {title}
                    </DialogTitle>
                  ) : (
                    <></>
                  )}
                  {description ? (
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>
                  ) : (
                    <></>
                  )}
                  {children ? <div className="mt-2">{children}</div> : <></>}
                </div>
              </div>
            </div>
            {actions?.length ? (
              <div className="bg-gray-50 px-4 pb-3 sm:flex sm:flex-row-reverse sm:px-6">
                <ModalActions
                  actions={actions}
                  alignment={actionsAlignment}
                  primaryButtonRef={primaryButtonRef}
                />
              </div>
            ) : (
              <></>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
